import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import './ExportFieldSelectionModal.css';

const TICKET_FIELD_GROUPS = {
    'Table Fields': [
        { key: 'orderId', label: 'Order ID', defaultSelected: true },
        { key: 'iboNumber', label: 'IBO Number', defaultSelected: true },
        { key: 'iboName', label: 'IBO Name', defaultSelected: true },
        { key: 'eventName', label: 'Event Name', defaultSelected: true },
        { key: 'eventStartDate', label: 'Event Start Date', defaultSelected: true },
        { key: 'eventEndDate', label: 'Event End Date', defaultSelected: true },
        { key: 'eventTimezone', label: 'Event Timezone', defaultSelected: true },
        { key: 'total', label: 'Total Charged', defaultSelected: true },
        { key: 'netAmount', label: 'Net Amount', defaultSelected: true },
        { key: 'status', label: 'Status', defaultSelected: true },
        { key: 'paymentMethod', label: 'Payment Method', defaultSelected: true },
        { key: 'purchaseSource', label: 'Purchase Source', defaultSelected: true },
        { key: 'totalTickets', label: 'Total Tickets', defaultSelected: true },
        { key: 'teamName', label: 'Team Name', defaultSelected: true },
        { key: 'uplinePlatinumIbo', label: 'Upline Platinum IBO', defaultSelected: true },
        { key: 'uplinePlatinumIboName', label: 'Upline Platinum Name', defaultSelected: true },
        { key: 'createdAt', label: 'Created At', defaultSelected: true },
    ],
    'Detail Fields': [
        { key: 'paymentId', label: 'Payment ID' },
        { key: 'eventId', label: 'Event ID' },
        { key: 'amount', label: 'Amount (Subtotal)' },
        { key: 'tax', label: 'Tax' },
        { key: 'serviceCharge', label: 'Service Charge' },
        { key: 'currency', label: 'Currency' },
        { key: 'paymentProvider', label: 'Payment Provider' },
    ],
    'Payment Details (Card & Fees)': [
        { key: 'cardLast4', label: 'Card Last 4 Digits' },
        { key: 'cardBrand', label: 'Card Brand' },
        { key: 'cardType', label: 'Card Type' },
        { key: 'invoiceNumber', label: 'Invoice Number' },
        { key: 'receiptUrl', label: 'Receipt URL' },
        { key: 'processingFee', label: 'Processing Fee' },
        { key: 'processingFeeCurrency', label: 'Fee Currency' },
        { key: 'feesFetchedAt', label: 'Fees Fetched At' },
    ],
};

const ExportFieldSelectionModal = ({ show, onClose, onExport, format, fieldGroups, title, note }) => {
    const effectiveGroups = useMemo(() => fieldGroups || TICKET_FIELD_GROUPS, [fieldGroups]);

    const buildDefaults = useMemo(() => {
        const defaults = {};
        Object.values(effectiveGroups).forEach((fields) => {
            fields.forEach((f) => {
                defaults[f.key] = !!f.defaultSelected;
            });
        });
        return defaults;
    }, [effectiveGroups]);

    const [selectedFields, setSelectedFields] = useState(buildDefaults);

    useEffect(() => {
        setSelectedFields(buildDefaults);
    }, [buildDefaults]);

    const handleToggle = (field) => {
        setSelectedFields(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const handleSelectAll = (group) => {
        const groupFields = effectiveGroups[group];
        const allSelected = groupFields.every(f => selectedFields[f.key]);
        const newState = { ...selectedFields };
        groupFields.forEach(f => {
            newState[f.key] = !allSelected;
        });
        setSelectedFields(newState);
    };

    const handleExport = () => {
        const fields = Object.keys(selectedFields).filter(key => selectedFields[key]);
        onExport(fields, format);
    };

    if (!show) return null;

    return (
        <div className="export-modal-overlay">
            <div className="export-modal">
                <div className="export-modal-header">
                    <h2>{title || 'Select Fields to Export'} ({format})</h2>
                    <button className="close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                <div className="export-modal-body">
                    {note !== null && (
                        <p className="export-note">
                            <strong>Note:</strong> {note || 'Selecting payment detail fields (Card & Fees) will fetch detailed information for each payment, which may take longer for large exports.'}
                        </p>
                    )}
                    
                    {Object.entries(effectiveGroups).map(([groupName, fields]) => (
                        <div key={groupName} className="field-group">
                            <div className="group-header">
                                <h3>{groupName}</h3>
                                <button 
                                    className="select-all-btn"
                                    onClick={() => handleSelectAll(groupName)}
                                >
                                    {fields.every(f => selectedFields[f.key]) ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            <div className="field-list">
                                {fields.map(field => (
                                    <label key={field.key} className="field-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedFields[field.key]}
                                            onChange={() => handleToggle(field.key)}
                                        />
                                        <span className="checkbox-custom">
                                            {selectedFields[field.key] && <FontAwesomeIcon icon={faCheck} />}
                                        </span>
                                        <span className="field-label">{field.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                
                <div className="export-modal-footer">
                    <button className="btn-cancel" onClick={onClose}>Cancel</button>
                    <button className="btn-export" onClick={handleExport}>
                        Export {format}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportFieldSelectionModal;
